import type { Session } from '@supabase/supabase-js';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@nuberu/ui';
import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';

import { ApiError } from '@/lib/api';

import { fetchAiUsage, type AiUsageSummary } from './fetch-ai-usage';
import { formatCount, formatTokens, formatUsd } from './format';

const DAY_OPTIONS = [7, 30, 90] as const;

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: AiUsageSummary };

const Stat = ({ label, value }: { label: string; value: string }): ReactElement => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs uppercase tracking-wide text-text-secondary">{label}</span>
    <span className="font-display text-2xl font-bold text-text-primary">{value}</span>
  </div>
);

const Th = ({ children, right }: { children: ReactNode; right?: boolean }): ReactElement => (
  <th
    className={`pb-2 text-xs font-medium uppercase tracking-wide text-text-secondary ${
      right ? 'text-right' : 'text-left'
    }`}
  >
    {children}
  </th>
);

const Td = ({ children, right }: { children: ReactNode; right?: boolean }): ReactElement => (
  <td className={`py-2 text-sm text-text-primary ${right ? 'text-right tabular-nums' : ''}`}>
    {children}
  </td>
);

export const AiUsagePanel = ({ session }: { session: Session }): ReactElement => {
  const [days, setDays] = useState<number>(30);
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(
    (selectedDays: number, signal?: AbortSignal): void => {
      setState({ status: 'loading' });
      void fetchAiUsage(session, selectedDays, signal)
        .then((data) => {
          if (!signal?.aborted) setState({ status: 'ready', data });
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          const message =
            err instanceof ApiError
              ? err.status === 403
                ? 'Tu cuenta no tiene acceso al backoffice.'
                : err.message
              : 'No pudimos cargar el uso de IA.';
          setState({ status: 'error', message });
        });
    },
    [session],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(days, controller.signal);
    return () => controller.abort();
  }, [load, days]);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>Uso de IA</CardTitle>
          <div className="flex items-center gap-1">
            {DAY_OPTIONS.map((option) => (
              <Button
                key={option}
                variant={option === days ? 'primary' : 'neutral'}
                size="sm"
                onClick={() => setDays(option)}
              >
                {option}d
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {state.status === 'loading' ? (
            <p className="py-4 text-sm text-text-secondary">Cargando uso…</p>
          ) : state.status === 'error' ? (
            <p className="py-4 text-sm text-status-warning" role="alert">
              {state.message}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label="Solicitudes" value={formatCount(state.data.totals.requests)} />
              <Stat label="Tokens entrada" value={formatTokens(state.data.totals.inputTokens)} />
              <Stat label="Tokens salida" value={formatTokens(state.data.totals.outputTokens)} />
              <Stat
                label="Coste est. (USD)"
                value={formatUsd(state.data.totals.estimatedCostMicros)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {state.status === 'ready' ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Por proveedor</CardTitle>
            </CardHeader>
            <CardContent>
              {state.data.byProvider.length === 0 ? (
                <p className="py-2 text-sm text-text-secondary">Sin datos todavía.</p>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-[1.5px] border-(color:--nuberu-ink)">
                      <Th>Proveedor · modelo</Th>
                      <Th right>Solicitudes</Th>
                      <Th right>Tokens</Th>
                      <Th right>Coste est.</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.byProvider.map((row) => (
                      <tr
                        key={`${row.provider}:${row.model}`}
                        className="border-b border-(color:--nuberu-line-soft) last:border-b-0"
                      >
                        <Td>
                          <Badge tone="neutral">{row.provider}</Badge>{' '}
                          <code className="font-mono text-xs">{row.model}</code>
                        </Td>
                        <Td right>{formatCount(row.requests)}</Td>
                        <Td right>{formatTokens(row.inputTokens + row.outputTokens)}</Td>
                        <Td right>{formatUsd(row.estimatedCostMicros)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top 10 operadores por coste</CardTitle>
            </CardHeader>
            <CardContent>
              {state.data.topOperators.length === 0 ? (
                <p className="py-2 text-sm text-text-secondary">Sin datos todavía.</p>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-[1.5px] border-(color:--nuberu-ink)">
                      <Th>Operador</Th>
                      <Th right>Solicitudes</Th>
                      <Th right>Tokens</Th>
                      <Th right>Coste est.</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.topOperators.map((row, index) => (
                      <tr
                        key={row.operatorId}
                        className="border-b border-(color:--nuberu-line-soft) last:border-b-0"
                      >
                        <Td>
                          <span className="text-text-secondary">{index + 1}.</span>{' '}
                          {row.operatorName}{' '}
                          <span className="text-text-secondary">({row.operatorSlug})</span>
                        </Td>
                        <Td right>{formatCount(row.requests)}</Td>
                        <Td right>{formatTokens(row.inputTokens + row.outputTokens)}</Td>
                        <Td right>{formatUsd(row.estimatedCostMicros)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-text-secondary">
            Coste estimado a partir de tarifas por modelo (no facturación). Desde{' '}
            {state.data.window.since.slice(0, 10)}.
          </p>
        </>
      ) : null}
    </div>
  );
};
